import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Menu, LogOut, User as UserIcon, Bell, Crown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SidebarContent } from "@/components/Sidebar";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";

export function AppShell({ title, children }) {
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  usePageTitle(title);

  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[240px] border-r border-border bg-white shadow-sm md:block">
        <SidebarContent />
      </aside>

      <div className="md:pl-[240px] min-w-0 max-w-full">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-white/95 px-3.5 sm:px-6 md:px-8 backdrop-blur-md">
          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:bg-elevated md:hidden"
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
              >
                <Menu className="h-4 w-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[240px] border-border bg-white p-0">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* Mobile logo — only show when no title */}
          {!title && (
            <div className="flex items-center gap-2 md:hidden">
              <Logo showText={false} />
            </div>
          )}

          {/* Page title — visible on all sizes; on mobile replaces logo */}
          {title && (
            <h1 className="truncate font-display text-sm font-semibold text-foreground sm:text-base" data-testid="page-title">
              {title}
            </h1>
          )}

          {/* Right actions */}
          <div id="tour-topbar-actions" className="ml-auto flex items-center gap-2">
            {/* Subtle Plan Badge if active */}
            {subscription?.active && (
              <Link
                to="/pricing"
                className="hidden h-9 items-center gap-1.5 rounded-full border border-border bg-surface px-3.5 text-xs font-semibold text-foreground transition-colors hover:bg-elevated sm:inline-flex"
                data-testid="header-plan-badge"
              >
                <Crown className="h-3.5 w-3.5 text-accent" />
                <span>{subscription.plan_name}</span>
              </Link>
            )}

            {/* Bell */}
            <button className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-surface text-muted-foreground transition-colors hover:bg-elevated hover:text-foreground">
              <Bell className="h-4 w-4" />
            </button>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex h-9 items-center gap-2 rounded-xl border border-border bg-surface pl-1 pr-3 transition-colors hover:bg-elevated"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback
                      className="text-xs font-bold text-white"
                      style={{ background: "linear-gradient(135deg, #E8522A, #F2744A)" }}
                    >
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden text-xs font-medium text-foreground sm:inline-block">
                    {user?.name?.split(" ")[0] || "Account"}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-xl border-border bg-white shadow-lg">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-xs font-semibold text-foreground">{user?.name}</p>
                    <p className="text-[11px] text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/pricing" className="cursor-pointer text-xs">
                    Plans &amp; Billing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer text-xs">
                    <UserIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                    Creator Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer text-xs">
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-xs text-destructive">
                  <LogOut className="mr-2 h-3.5 w-3.5" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto max-w-[1400px] px-3 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 fade-in min-w-0 w-full pb-[max(1.5rem,calc(1rem+env(safe-area-inset-bottom,0px)))]">
          {children}
        </main>
      </div>
    </div>
  );
}
